/* eslint-disable object-curly-newline */
import { jsx } from "slate-hyperscript";
import { Transforms } from "slate";
import {
  ELEMENT_BLOCK_QUOTE,
  ELEMENT_BULLETED_LIST,
  ELEMENT_CODE,
  ELEMENT_HEADING_FOUR,
  ELEMENT_HEADING_ONE,
  ELEMENT_HEADING_THREE,
  ELEMENT_HEADING_TWO,
  ELEMENT_IMAGE,
  ELEMENT_LINK,
  ELEMENT_LIST_ITEM,
  ELEMENT_NUMBERED_LIST,
  ELEMENT_PARAGRAPH,
  ELEMENT_SUBSCRIBE_BUTTON,
} from "./Element";

const ELEMENT_TAGS = {
  A: (el) => ({ type: ELEMENT_LINK, url: el.getAttribute("href") }),
  BLOCKQUOTE: () => ({ type: ELEMENT_BLOCK_QUOTE }),
  H1: () => ({ type: ELEMENT_HEADING_ONE }),
  H2: () => ({ type: ELEMENT_HEADING_TWO }),
  H3: () => ({ type: ELEMENT_HEADING_THREE }),
  H4: () => ({ type: ELEMENT_HEADING_FOUR }),
  H5: () => ({ type: ELEMENT_HEADING_FOUR }),
  H6: () => ({ type: ELEMENT_HEADING_FOUR }),
  IMG: (el) => ({ type: ELEMENT_IMAGE, url: el.getAttribute("src") }),
  LI: () => ({ type: ELEMENT_LIST_ITEM }),
  OL: () => ({ type: ELEMENT_NUMBERED_LIST }),
  P: () => ({ type: ELEMENT_PARAGRAPH }),
  PRE: () => ({ type: ELEMENT_CODE }),
  UL: () => ({ type: ELEMENT_BULLETED_LIST }),
};

// COMPAT: `B` is omitted here because Google Docs uses `<b>` in weird ways.
const TEXT_TAGS = {
  CODE: () => ({ code: true }),
  DEL: () => ({ strikethrough: true }),
  EM: () => ({ italic: true }),
  I: () => ({ italic: true }),
  S: () => ({ strikethrough: true }),
  STRONG: () => ({ bold: true }),
  U: () => ({ underline: true }),
};

export const deserialize = (el) => {
  if (el.nodeType === 3) {
    return el.textContent;
  } else if (el.nodeType !== 1) {
    return null;
  } else if (el.nodeName === "BR") {
    return "\n";
  }

  const { nodeName } = el;
  let parent = el;

  if (nodeName === "PRE" && el.childNodes[0] && el.childNodes[0].nodeName === "CODE") {
    parent = el.childNodes[0];
  }
  let children = Array.from(parent.childNodes).map(deserialize).flat();

  if (children.length === 0) {
    children = [{ text: "" }];
  }

  if (el.nodeName === "BODY") {
    return jsx("fragment", {}, children);
  }

  if (ELEMENT_TAGS[nodeName]) {
    const attrs = ELEMENT_TAGS[nodeName](el);
    return jsx("element", attrs, children);
  }

  if (TEXT_TAGS[nodeName]) {
    const attrs = TEXT_TAGS[nodeName](el);
    return children.map((child) => jsx("text", attrs, child));
  }

  return children;
};

const withHtml = (editor) => {
  const { insertData, isInline, isVoid } = editor;

  editor.isInline = (element) => {
    return element.type === "link" ? true : isInline(element);
  };

  editor.isVoid = (element) => {
    return element.type === "image" || element.type === ELEMENT_SUBSCRIBE_BUTTON ? true : isVoid(element);
  };

  editor.insertData = (data) => {
    const html = data.getData("text/html");

    if (html) {
      const parsed = new DOMParser().parseFromString(html, "text/html");
      const fragments = deserialize(parsed.body);
      const safeFragments = sanitizeFragment(fragments);
      try {
        Transforms.insertFragment(editor, safeFragments);
      } catch (e) {
        console.log("Error trying to paste html content: ", e);
      }

      return;
    }

    insertData(data);
  };

  return editor;
};

export default withHtml;

function sanitizeFragment(fragments) {
  const result = fragments;

  for (let i = 0; i < fragments.length; i++) {
    if (fragments[i].type === ELEMENT_BULLETED_LIST && fragments[i].children && fragments[i].children.length > 0) {
      result[i].children = fixOrRemoveEmptyListItems(fragments[i].children);
    }
  }

  return result;
}

function fixOrRemoveEmptyListItems(list) {
  const result = list;

  for (let i = 0; i < list.length; i++) {
    if (!list[i].type && list[i].text && list[i].text === "\n") {
      result[i] = null;
    }
  }

  return result.filter(Boolean);
}
